import { useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Calendar, MessageSquare, Star, Clock, LogOut, Home } from 'lucide-react';

export const AdminLayout = ({ children, title }) => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5ce1e6]"></div>
      </div>
    );
  }

  if (!user) return null;

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/admin/appointments', label: 'Appointments', icon: <Calendar className="w-5 h-5" /> },
    { path: '/admin/blocked-slots', label: 'Blocked Slots', icon: <Clock className="w-5 h-5" /> },
    { path: '/admin/testimonials', label: 'Testimonials', icon: <Star className="w-5 h-5" /> },
    { path: '/admin/messages', label: 'Messages', icon: <MessageSquare className="w-5 h-5" /> },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar */}
      <aside className="w-64 admin-sidebar p-4 flex flex-col">
        <div className="mb-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white">
              <img src="/images/Logo4.png" alt="BN Physio" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-semibold">BN Physio Admin</span>
          </Link>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-link ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-4 border-t border-slate-700">
          <div className="px-3 py-2 text-sm text-slate-400 mb-2">
            {user.name}
          </div>
          <button
            onClick={handleLogout}
            className="admin-nav-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {title}
          </h1>
        </div>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
